<script setup>
import { nextTick, onMounted, ref } from "vue";
import Sidebar from "./Sidebar.vue";
import { useI18n } from "vue-i18n";
import { MagicIcon } from "@Icons/index";
import AppLink from "@Components/AppLink.vue";
import { PrimaryButton } from "@Components/Button";
import { HistoryIcon, TrashIcon } from "lucide-vue-next";
import { useForm } from "@/form";
import { useRoute, useRouter } from "vue-router";
import Processing from "@Components/Animation/Processing.vue";
import { DestroyModal } from "@Components/Modal";
import Badge from "@Components/Badge.vue";
import { useStore } from "@/Stores/singleModeGenerations";

const { t: __ } = useI18n();
const route = useRoute();
const router = useRouter();

const store = useStore();

const loadMoreElement = ref(null);
const destroyModal = ref(null);

const destroyForm = useForm({
    id: null,
});

const current = (id) => {
    return route.params.id == id;
};

const destroy = () => {
    if (destroyForm.processing) return;
    destroyForm
        .post("single_mode_destroy")
        .then(async () => {
            destroyModal.value.hide();

            store.refresh();

            if (route.name === "single-mode.show" && route.params.id == destroyForm.id) {
                router.push({ name: "single-mode.create", force: true });
            }
        })
        .catch(() => {
            destroyModal.value.hide();

            $notify({
                title: "An error occurred while deleting.",
                type: "error",
            });
        });
};

onMounted(async () => {
    await store.fetch();

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                store.loadMore();
            }
        });
    });
    nextTick(() => {
        observer.observe(loadMoreElement.value);
    });
});
</script>

<template>
    <Sidebar :back="{ to: { name: 'dashboard' }, label: __('Retour au Dashboard') }">
        <div class="flex-none px-6">
            <PrimaryButton :as="AppLink" :to="{ name: 'single-mode.create', force: true }" class="w-full" :z="1" :icon="MagicIcon">
                {{ __("Créer un nouveau contenu") }}
            </PrimaryButton>
        </div>

        <div class="flex items-center gap-x-1.5 px-6 py-2 mt-3 text-sm leading-6 text-gray-900 border-l-2 border-transparent group dark:text-gray-200">
            <div class="flex items-center gap-x-3">
                <HistoryIcon class="size-5 shrink-0" aria-hidden="true" />
                {{ __("Historique") }}
            </div>
        </div>

        <div class="flex-grow overflow-y-auto flex flex-col">
            <div v-if="store.processing && store.generations.length === 0" class="flex justify-center items-center">
                <Processing class="text-gray-600 dark:text-gray-400" />
            </div>
            <div v-else-if="!store.processing || store.generations.length > 0" class="flex flex-col flex-1 divide-y divide-gray-300 dark:divide-gray-800">
                <div v-if="store.generations.length > 0">
                    <div v-for="generation in store.generations" :key="generation.id">
                        <AppLink
                            :to="{ name: 'single-mode.show', params: { id: generation.id } }"
                            :class="[
                                current(generation.id)
                                    ? 'border-gray-950 bg-gray-50 dark:bg-gray-800 dark:border-gray-600'
                                    : [
                                          'hover:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 dark:hover:border-gray-700',
                                          'border-transparent',
                                      ],
                                'text-gray-900 dark:text-gray-200 group block px-6 py-2 text-sm border-l-2',
                            ]">
                            <div class="flex items-center gap-x-1.5">
                                <div class="flex-1 truncate">
                                    {{ generation.title }}
                                </div>

                                <div class="hidden flex-none cursor-pointer group-hover:block">
                                    <TrashIcon
                                        @click.prevent="
                                            destroyForm.id = generation.id;
                                            destroyModal.show();
                                        "
                                        class="size-4 shrink-0 text-gray-600 dark:text-gray-400 hover:text-red-500"
                                        aria-hidden="true" />
                                </div>
                            </div>
                            <div class="mt-1.5">
                                <Badge v-if="generation.status === 'idle' || generation.status === 'pending'" color="yellow">
                                    {{ __("En cours") }}
                                </Badge>
                                <Badge v-else-if="generation.status === 'failed'" color="red">
                                    {{ __("Échoué") }}
                                </Badge>
                                <Badge v-else-if="generation.status === 'success'" color="green">
                                    {{ __("Terminé") }}
                                </Badge>
                                <Badge v-else>
                                    {{ generation.status }}
                                </Badge>
                            </div>
                        </AppLink>
                    </div>
                </div>
                <div v-else class="flex justify-center items-center mt-6">
                    {{ __("Aucun article trouvé") }}
                </div>
            </div>
            <div ref="loadMoreElement"></div>
        </div>

        <div v-if="store.processing && store.generations.length > 0" class="flex justify-center items-center mt-3">
            <Processing class="text-gray-600 dark:text-gray-400" />
        </div>

        <DestroyModal
            ref="destroyModal"
            :title="__('Supprimer la génération #{id}', { id: destroyForm.id })"
            :description="__('Êtes-vous sûr de vouloir supprimer la génération #{id} ? Cette action ne peut pas être annulée.', { id: destroyForm.id })"
            @destroy="destroy" />
    </Sidebar>
</template>
